import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { PageContainer, PageHeader, EmptyState } from "@/components/app/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Plus, Trash, MagnifyingGlass, CaretUp, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function RolesPage() {
    const [roles, setRoles] = useState([]);
    const [forms, setForms] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", code: "", description: "", permissions: "" });
    const [search, setSearch] = useState("");
    const [appFilter, setAppFilter] = useState("__all__");
    const [typeFilter, setTypeFilter] = useState("__all__");
    const [sortBy, setSortBy] = useState("name");
    const [sortDir, setSortDir] = useState(1);

    const load = async () => {
        try {
            const [r, f] = await Promise.all([api.get("/roles"), api.get("/forms").catch(() => ({ data: [] }))]);
            setRoles(r.data); setForms(f.data);
        } catch (e) { toast.error(extractError(e)); }
    };
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        try {
            await api.post("/roles", {
                name: form.name, code: form.code, description: form.description,
                permissions: form.permissions.split(",").map((p) => p.trim()).filter(Boolean),
            });
            toast.success("Role created");
            setForm({ name: "", code: "", description: "", permissions: "" });
            setOpen(false); load();
        } catch (err) { toast.error(extractError(err)); }
    };

    const del = async (r) => {
        if (r.is_system) return toast.error("System roles cannot be deleted");
        if (!confirm("Delete role?")) return;
        try { await api.delete(`/roles/${r.id}`); toast.success("Role deleted"); load(); }
        catch (e) { toast.error(extractError(e)); }
    };

    const filteredRoles = useMemo(() => {
        let arr = roles;
        const q = search.trim().toLowerCase();
        if (q) arr = arr.filter((r) => r.name.toLowerCase().includes(q) || (r.code || "").toLowerCase().includes(q));
        if (appFilter !== "__all__") {
            const f = forms.find((x) => x.id === appFilter);
            arr = arr.filter((r) => r.form_id === appFilter || (f && (r.code || "").startsWith(`form:${f.form_code}`)));
        }
        if (typeFilter === "system") arr = arr.filter((r) => r.is_system);
        if (typeFilter === "custom") arr = arr.filter((r) => !r.is_system);
        return [...arr].sort((a, b) => {
            const av = String(a[sortBy] ?? "").toLowerCase();
            const bv = String(b[sortBy] ?? "").toLowerCase();
            return av.localeCompare(bv) * sortDir;
        });
    }, [roles, search, appFilter, typeFilter, sortBy, sortDir, forms]);

    const toggleSort = (col) => {
        if (sortBy === col) setSortDir(sortDir === 1 ? -1 : 1);
        else { setSortBy(col); setSortDir(1); }
    };
    const SortIcon = ({ col }) => sortBy === col ? (sortDir === 1 ? <CaretUp size={10} className="inline ml-1" /> : <CaretDown size={10} className="inline ml-1" />) : null;

    return (
        <PageContainer>
            <PageHeader
                overline="Access"
                title="Roles"
                description="RBAC building blocks. Per-form roles are auto-generated when you create forms."
                actions={
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="new-role-btn"><Plus size={16} className="mr-2" /> New role</Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-sm" data-testid="role-dialog">
                            <DialogHeader><DialogTitle>New role</DialogTitle></DialogHeader>
                            <form onSubmit={save} className="space-y-4">
                                <div><Label className="label-overline">Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-sm" data-testid="role-name-input" /></div>
                                <div><Label className="label-overline">Code</Label><Input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="mt-2 rounded-sm font-mono" placeholder="manager" data-testid="role-code-input" /></div>
                                <div><Label className="label-overline">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2 rounded-sm" data-testid="role-desc-input" /></div>
                                <div>
                                    <Label className="label-overline">Permissions (comma-separated)</Label>
                                    <Textarea value={form.permissions} onChange={(e) => setForm({ ...form, permissions: e.target.value })} className="mt-2 rounded-sm font-mono text-xs" placeholder="form:contacts:read, form:contacts:create" data-testid="role-perms-input" />
                                    <p className="text-xs text-muted-foreground mt-1">Format: <span className="font-mono">resource:item:action</span>. Use <span className="font-mono">*</span> for wildcards.</p>
                                </div>
                                <DialogFooter><Button type="submit" className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="save-role-btn">Create role</Button></DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                }
                testid="roles-header"
            />

            {/* Filter bar */}
            <div className="flex flex-wrap gap-2 mb-6 items-center" data-testid="roles-filter-bar">
                <div className="relative w-full sm:w-72">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input placeholder="Search by name or code…" value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-sm pl-9" data-testid="roles-search-input" />
                </div>
                <Select value={appFilter} onValueChange={setAppFilter}>
                    <SelectTrigger className="w-56 rounded-sm" data-testid="roles-app-filter"><SelectValue placeholder="Filter by app" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all__">All apps</SelectItem>
                        {forms.map((f) => <SelectItem key={f.id} value={f.id}>{f.form_name}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="w-36 rounded-sm" data-testid="roles-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all__">All types</SelectItem>
                        <SelectItem value="system">System</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {filteredRoles.length === 0 ? (
                <EmptyState icon={ShieldCheck} title={roles.length === 0 ? "No roles yet" : "No roles match"} description={roles.length === 0 ? "Create custom roles or define a form to auto-generate them." : "Try adjusting filters."} testid="roles-empty" />
            ) : (
                <div className="brut-section overflow-hidden">
                    <table className="w-full" data-testid="roles-table">
                        <thead><tr>
                            <th className="data-header text-left cursor-pointer select-none" onClick={() => toggleSort("name")} data-testid="roles-sort-name">Name<SortIcon col="name" /></th>
                            <th className="data-header text-left cursor-pointer select-none" onClick={() => toggleSort("code")} data-testid="roles-sort-code">Code<SortIcon col="code" /></th>
                            <th className="data-header text-left">Type</th>
                            <th className="data-header text-left">Permissions</th>
                            <th className="data-header"></th>
                        </tr></thead>
                        <tbody>
                            {filteredRoles.map((r) => (
                                <tr key={r.id} className="hover:bg-secondary/50" data-testid={`role-row-${r.id}`}>
                                    <td className="data-cell font-medium">{r.name}</td>
                                    <td className="data-cell font-mono text-xs">{r.code}</td>
                                    <td className="data-cell">
                                        {r.is_system ? <Badge variant="outline" className="rounded-sm font-mono text-xs">System</Badge>
                                                     : <Badge className="rounded-sm font-mono text-xs">Custom</Badge>}
                                    </td>
                                    <td className="data-cell">
                                        <div className="flex flex-wrap gap-1 max-w-md">
                                            {(r.permissions || []).slice(0, 3).map((p) => (
                                                <code key={p} className="text-[10px] bg-secondary px-1.5 py-0.5 rounded-sm">{p}</code>
                                            ))}
                                            {r.permissions?.length > 3 && <span className="text-xs text-muted-foreground">+{r.permissions.length - 3}</span>}
                                        </div>
                                    </td>
                                    <td className="data-cell text-right">
                                        {!r.is_system && (
                                            <Button variant="ghost" size="icon" className="rounded-sm" onClick={() => del(r)} data-testid={`del-role-${r.id}`}><Trash size={16} /></Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </PageContainer>
    );
}
